/**
 * Global Search Configuration
 *
 * Defines default behaviors and settings for search functionality across the application.
 */

export interface GlobalSearchConfig {
  /** Default debounce delay in milliseconds for search input */
  debounceMs: number;
  /** Minimum search term length before triggering search */
  minSearchLength: number;
  /** Maximum number of search results to return per entity */
  maxResults: number;
  /** Default search result limit for pagination */
  defaultLimit: number;
  /** Whether to enable search result highlighting */
  enableHighlighting: boolean;
  /** Case sensitivity for search operations */
  caseSensitive: boolean;
  /** Whether to split search terms by spaces and require all terms to match */
  multiTermSearch: boolean;
  /** Highlighting style configuration */
  highlightStyle?: {
    /** CSS class name for highlighted terms */
    className?: string;
    /** Inline style for highlighted terms (if not using className) */
    style?: React.CSSProperties;
  };
}

/**
 * Unified Query Options Interface
 *
 * This interface ensures consistent parameter naming across all generators
 * and resolves the filter/filters naming misalignment between table client
 * and schema generators.
 */
export interface UnifiedQueryOptions {
  /** Pagination limit */
  limit?: number;
  /** Pagination offset */
  offset?: number;
  /** Field to sort by */
  sortBy?: string;
  /** Sort order direction */
  sortOrder?: 'asc' | 'desc';
  /** Filter criteria as key-value pairs (standardized as 'filters' plural) */
  filters?: Record<string, any>;
  /** Select specific fields (for schema generator compatibility) */
  select?: string;
  /** Order configuration (for schema generator compatibility) */
  order?: { column: string; ascending?: boolean }[];
  /** Return single result (for schema generator compatibility) */
  single?: boolean;
}

/**
 * Legacy filter interface for backward compatibility
 * @deprecated Use UnifiedQueryOptions with 'filters' instead
 */
export interface LegacyFilterOptions {
  /** @deprecated Use 'filters' instead */
  filter?: Record<string, any>;
}

export const globalSearchConfig: GlobalSearchConfig = {
  debounceMs: 300,
  minSearchLength: 2,
  maxResults: 100,
  defaultLimit: 100,
  enableHighlighting: true,
  caseSensitive: false,
  multiTermSearch: true,
  highlightStyle: {
    className: 'search-highlight',
    // Default inline style as fallback if className is not defined in CSS
    style: {
      backgroundColor: 'yellow',
      color: 'black',
      fontWeight: 'bold',
      padding: '0 2px',
      borderRadius: '2px',
    }
  }
};